import { Menu, Transition } from '@headlessui/react';
import {
  DownloadIcon,
  ExclamationCircleIcon,
  ExternalLinkIcon,
  FilmIcon,
  PlayIcon,
  RefreshIcon,
  ShareIcon,
  SparklesIcon,
  ThumbDownIcon,
  ThumbUpIcon,
} from '@heroicons/react/outline';
import { DotsVerticalIcon, TrashIcon } from '@heroicons/react/solid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import { Fragment, memo, useEffect, useRef, useState } from 'react';
import { FaExpand } from 'react-icons/fa6';
import { toast } from 'sonner';
import { Image } from 'types';

import clsxm from '@/lib/clsxm';
import formatDate from '@/lib/formatDate';
import {
  buildMediaFileName,
  resolveMediaExtension,
} from '@/lib/mediaLibrary/mediaItems';
import { saveVideoEditSeed } from '@/lib/videoEditSeed';

import { useMediaTileSelection } from '@/components/media-library/MediaSelectionProvider';
import MediaTileSelectionLayer, {
  mediaTileSelectionClassName,
} from '@/components/media-library/MediaTileSelectionLayer';
import { useMediaFileDownload } from '@/components/media-library/useMediaFileDownload';

import {
  canUpgradeMiniMaxVideo,
  type MiniMaxVideoUpgradeQuote,
} from '@/constants/minimaxVideoUpgrade';
import type { Seedance25EditTier } from '@/constants/videoModels';
import type { VideoUpscaleSource } from '@/constants/videoUpscale';
import { useAnalytics } from '@/contexts/Analytics';
import axiosInstance from '@/utils/httpClient';
import { toastError } from '@/utils/toastError';
import { useUser } from '@/utils/useUser';

import ShareVideoModal from './ShareVideoModal';
import VideoEditModal from './VideoEditModal';
import { CopyBtn } from '../atoms/CopyBtn';
import Modal from '../atoms/Modal';
import Toggle from '../atoms/Toggle';
import Button from '../buttons/Button';
import { SubscriptionPromptOverlay } from '../shared/SubscriptionPromptOverlay';
import Spinner from '../Spinner';

const MiniMaxVideoUpgradeModal = dynamic(
  () => import('./MiniMaxVideoUpgradeModal'),
);

interface VideoOutputProps {
  video: Partial<Image> & {
    url?: string;
  };
  viewMode?: {
    layout: 'grid' | 'row';
    size: 'small' | 'medium' | 'large';
    fill: 'aspect-ratio' | 'square';
  };
}

// Helper function to detect iOS
const isIOS = () => {
  if (typeof window === 'undefined') return false;

  // Check for iOS devices including iPad
  const isIOSDevice =
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

  // Also check for iPad on iOS 13+ which reports as Mac
  const isIPadOS =
    navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

  return isIOSDevice || isIPadOS;
};

const FAILED_VIDEO_URL = 'https://videos.easy-peasy.ai/202507251214.mp4';

const VideoOutput = ({ video }: VideoOutputProps) => {
  const { t } = useLocale('ai-images');
  const { user, isFreePlan } = useUser();
  const router = useRouter();
  const [isPlaying, setIsPlaying] = useState(false);
  // Multi-select (only inside a MediaSelectionProvider): while selecting, a
  // click selects the tile instead of playing it.
  const selection = useMediaTileSelection(video);
  const downloadMediaFile = useMediaFileDownload();
  const [showModal, setShowModal] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isiOS, setIsiOS] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [miniMaxUpgradeId, setMiniMaxUpgradeId] = useState<number | null>(null);
  // Which clip the AI editor should open on, and the cues to seed it with.
  // Set to the video itself for a fresh edit, or back to an edit's source when
  // re-running a previous set of instructions.
  const [editSeed, setEditSeed] = useState<{
    videoId: number;
    videoUrl: string;
    instructions?: any[];
    referenceImages?: string[];
    tier?: Seedance25EditTier;
    resolution?: string;
  } | null>(null);
  // Modal keeps the panel mounted for its 400ms leave transition, so the
  // editor has to survive `editSeed` going null or it fades out empty.
  const lastEditSeedRef = useRef(editSeed);
  if (editSeed) lastEditSeedRef.current = editSeed;
  const editSeedForRender = editSeed ?? lastEditSeedRef.current;
  const [shareWithTeam, setShareWithTeam] = useState(
    Boolean(video.share_with_team),
  );
  const queryClient = useQueryClient();
  const videoRef = useRef<HTMLVideoElement>(null);
  const modalVideoRef = useRef<HTMLVideoElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  // Queue state for free users
  const [isInQueue, setIsInQueue] = useState(() => {
    const queuedUntil = (video as any)._queuedUntil;
    return queuedUntil ? Date.now() < queuedUntil : false;
  });

  // Add analytics
  const analytics = useAnalytics();

  // Detect iOS on mount
  useEffect(() => {
    const ios = isIOS();
    setIsiOS(ios);
    // Modal videos start unmuted, preview always muted
    setIsMuted(false);
  }, []);

  // Check queue expiration
  useEffect(() => {
    const queuedUntil = (video as any)._queuedUntil;
    if (!queuedUntil || !isFreePlan) return;

    const checkQueue = () => {
      if (Date.now() >= queuedUntil) {
        setIsInQueue(false);
      }
    };

    // Check immediately
    checkQueue();

    // Check every 10 seconds
    const interval = setInterval(checkQueue, 10000);
    return () => clearInterval(interval);
  }, [(video as any)._queuedUntil, isFreePlan]);

  // Use either url or image_url
  const videoUrl = video.url || video.image_url;
  const isFailedGeneration = videoUrl === FAILED_VIDEO_URL;

  // Handle hover play/pause
  useEffect(() => {
    if (!isiOS && videoRef.current && !videoError) {
      if (isHovering && !showModal) {
        // Small delay before playing to avoid accidental plays
        hoverTimeoutRef.current = setTimeout(() => {
          if (videoRef.current && isHovering) {
            videoRef.current.play().catch(() => {
              // Silently fail if autoplay is blocked
            });
          }
        }, 200);
      } else {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }

    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, [isHovering, isiOS, showModal, videoError]);

  // Add mutation for voting
  const mutation = useMutation(
    (action: string) => {
      return axiosInstance.post('/api/images', {
        action,
        image_id: video.id,
      });
    },
    {
      onSuccess: (response) => {
        toast.success(response.data.message);
        // A downvote may have refunded credits — let credit counters refresh.
        if (response.data?.refunded && typeof window !== 'undefined') {
          window.dispatchEvent(new Event('credits-refreshed'));
        }
      },
    },
  );

  const shareWithTeamMutation = useMutation(
    (vars: { shareWithTeam: boolean }) => {
      return axiosInstance.post('/api/images', {
        action: 'SHARE_WITH_TEAM',
        image_id: video.id,
        shareWithTeam: vars.shareWithTeam,
      });
    },
  );

  const handleShareWithTeam = async () => {
    const nextValue = !shareWithTeam;
    setShareWithTeam(nextValue);
    try {
      await shareWithTeamMutation.mutateAsync({ shareWithTeam: nextValue });
      toast.success(t('Video settings saved successfully 👍'));
    } catch (error) {
      console.error(error);
      toast.error(t('Failed to save video settings 😞'));
    }
  };

  const handleDownload = () => {
    if (!videoUrl) return;
    void downloadMediaFile(
      videoUrl,
      buildMediaFileName(video, resolveMediaExtension(video, videoUrl)),
    );
  };

  // Rows produced by the Seedance 2.5 timestamp editor carry their source clip
  // and cues, which is what lets "Regenerate" reopen the editor on the
  // original instead of routing to a model the generator doesn't know about.
  const videoEdit =
    ((video as any).request?.videoEdit as
      | {
          sourceVideoId?: number;
          sourceVideoUrl?: string;
          instructions?: any[];
          referenceImages?: string[];
        }
      | undefined) || undefined;
  const isEditResult = Boolean(videoEdit?.sourceVideoUrl);

  // Same idea for extension results: they carry the clip they continued, so
  // "Regenerate" reopens the extend form on that source rather than handing
  // the generator a model title it has no catalogue entry for.
  const videoExtend =
    ((video as any).request?.videoExtend as
      | {
          sourceVideoId?: number | null;
          sourceVideoUrl?: string;
          extendModel?: string;
        }
      | undefined) || undefined;
  const isExtendResult = Boolean(videoExtend?.sourceVideoUrl);
  const videoUpscale = video.request?.videoUpscale as
    | VideoUpscaleSource
    | undefined;
  const miniMaxUpgrade = video.request?.minimaxVideoUpgrade as
    | MiniMaxVideoUpgradeQuote
    | undefined;
  const isUpscaleResult = Boolean(
    videoUpscale?.sourceVideoUrl || miniMaxUpgrade?.sourceVideoId,
  );

  // Editing runs on the caller's own clip (ownership is enforced again in
  // /api/edit-video), and a failed generation has nothing to edit.
  const canEditWithAI =
    !!video.id &&
    !!videoUrl &&
    !isFailedGeneration &&
    !video.isFromExample &&
    !!user?.id &&
    video.user_id === user.id;

  // Extension appends a new segment generated from this clip's last frame.
  // Same ownership posture as editing (/api/extend-video re-checks the row),
  // and a failed generation has nothing to continue.
  const canExtendVideo = canEditWithAI;
  const canUpgradeWithMiniMax = canEditWithAI && canUpgradeMiniMaxVideo(video);

  const handleMiniMaxUpgrade = () => {
    if (!video.id || !canUpgradeWithMiniMax) return;
    setShowModal(false);
    setMiniMaxUpgradeId(video.id);
  };

  const handleExtendVideo = () => {
    if (!video.id || !videoUrl) return;
    analytics.track('Video Extend Opened', { video_id: video.id });
    setShowModal(false);
    router.push(
      `/ai-videos?tab=extend-video&video=${encodeURIComponent(
        videoUrl,
      )}&video_id=${video.id}`,
    );
  };

  const handleUpscaleVideo = () => {
    if (!video.id || !videoUrl) return;
    analytics.track('Video Upscale Opened', { video_id: video.id });
    setShowModal(false);
    const params = new URLSearchParams({
      video: videoUrl,
      video_id: String(video.id),
    });
    router.push(`/ai-videos?tab=upscale-video&${params.toString()}`);
  };

  const handleEditWithAI = () => {
    if (!video.id || !videoUrl) return;
    analytics.track('Video AI Edit Opened', { video_id: video.id });
    // Closing the detail view first keeps the editor from stacking on top of
    // another headlessui Dialog, where the parent treats every click inside
    // the child as an outside click and unmounts both.
    setShowModal(false);
    setEditSeed({ videoId: video.id, videoUrl });
  };

  const handleRegenerate = () => {
    if (miniMaxUpgrade?.sourceVideoId) {
      setShowModal(false);
      setMiniMaxUpgradeId(miniMaxUpgrade.sourceVideoId);
      return;
    }
    if (videoUpscale?.sourceVideoUrl) {
      setShowModal(false);
      const params = new URLSearchParams({
        video: videoUpscale.sourceVideoUrl,
        upscale_model: videoUpscale.upscaleModel,
        resolution: video.request?.resolution || '1080p',
      });
      if (videoUpscale.sourceVideoId)
        params.set('video_id', String(videoUpscale.sourceVideoId));
      router.push(`/ai-videos?tab=upscale-video&${params.toString()}`);
      return;
    }

    // An edited clip re-runs through the editor, seeded with the source video
    // and the cues that produced it.
    if (isEditResult && videoEdit?.sourceVideoUrl && videoEdit.sourceVideoId) {
      analytics.track('Video Regenerate', { video_id: video.id });
      setShowModal(false);
      setEditSeed({
        videoId: videoEdit.sourceVideoId,
        videoUrl: videoEdit.sourceVideoUrl,
        instructions: videoEdit.instructions,
        referenceImages: videoEdit.referenceImages,
        tier: video.request?.tier,
        resolution: video.request?.resolution,
      });
      return;
    }
    // An edit of an UPLOADED clip has no gallery row to seed the modal with;
    // the Edit Video tool takes the URL and re-validates its host server-side,
    // and picks the cues up from the seed saved here.
    if (isEditResult && videoEdit?.sourceVideoUrl) {
      analytics.track('Video Regenerate', { video_id: video.id });
      setShowModal(false);
      saveVideoEditSeed({
        videoUrl: videoEdit.sourceVideoUrl,
        instructions: videoEdit.instructions,
        referenceImages: videoEdit.referenceImages,
        tier: video.request?.tier,
        resolution: video.request?.resolution,
      });
      router.push(
        `/ai-videos?tab=edit-video&video=${encodeURIComponent(
          videoEdit.sourceVideoUrl,
        )}`,
      );
      return;
    }

    // An extension re-runs through the extend form on the clip it continued.
    if (isExtendResult && videoExtend?.sourceVideoUrl) {
      analytics.track('Video Regenerate', { video_id: video.id });
      setShowModal(false);
      const params = new URLSearchParams({
        video: videoExtend.sourceVideoUrl,
        prompt: video.prompt || '',
      });
      if (videoExtend.sourceVideoId) {
        params.set('video_id', String(videoExtend.sourceVideoId));
      }
      router.push(`/ai-videos?tab=extend-video&${params.toString()}`);
      return;
    }

    // The generation params live in either `request` (ai-videos flow) or
    // `response_data` (create-video flow) depending on how the video was made.
    const req = ((video as any).request as Record<string, any>) || {};
    const resp = ((video as any).response_data as Record<string, any>) || {};

    analytics.track('Video Regenerate', { video_id: video.id });

    // Close the detail modal — when already on the target page the
    // navigation below won't unmount this component, so the modal must
    // be closed here.
    setShowModal(false);

    if (video.is_talking_video) {
      const uploadedVideo = req.originalVideo || resp.originalVideo || '';
      const image = uploadedVideo
        ? ''
        : req.originalImage || req.image || resp.image || '';
      const voiceId = req.voiceID || resp.voiceID || '';
      // Prefer the visible prompt; fall back to request.text. Skip the
      // "Audio file uploaded" placeholder that backs audio-upload videos.
      const candidateText = video.prompt || req.text || resp.text || '';
      const text = candidateText === 'Audio file uploaded' ? '' : candidateText;
      const audio = req.uploadedAudio || resp.uploadedAudio || '';
      const audioDuration =
        req.audioDurationSeconds ?? resp.audioDurationSeconds;
      const params = new URLSearchParams();
      // Video upload wins over image — they're mutually exclusive sources.
      if (uploadedVideo) params.set('video', uploadedVideo);
      else if (image) params.set('image', image);
      if (voiceId) params.set('voice_id', voiceId);
      // Text takes precedence: when a real prompt was used, regenerate
      // in text-to-speech mode rather than audio-upload mode — even if
      // the row also has uploadedAudio attached (TTS-generated audio
      // gets stored there too).
      if (text) {
        params.set('text', text);
      } else if (audio) {
        params.set('audio', audio);
        if (Number.isFinite(audioDuration) && audioDuration > 0) {
          params.set('audio_duration', String(audioDuration));
        }
      }
      const qs = params.toString();
      router.push(`/ai-talking-videos${qs ? `?${qs}` : ''}`);
      return;
    }

    const payload = {
      prompt: video.prompt || '',
      model: video.model || '',
      image: req.image || resp.image || '',
      tailImage: req.tailImage || resp.tailImage || '',
      referenceImages: req.referenceImages || resp.referenceImages || [],
      referenceAudios: req.referenceAudios || resp.referenceAudios || [],
      // Stored as an array by the Seedance/MiniMax branches and as a single
      // URL by the Gemini Omni edit branch — normalized on the way in.
      referenceVideos:
        req.referenceVideos ||
        resp.referenceVideos ||
        (req.referenceVideo || resp.referenceVideo
          ? [req.referenceVideo || resp.referenceVideo]
          : []),
      duration: req.duration ?? resp.duration ?? null,
      aspectRatio: req.aspectRatio || resp.aspectRatio || null,
      resolution: req.resolution || resp.resolution || null,
      generateAudio:
        typeof req.generateAudio === 'boolean'
          ? req.generateAudio
          : typeof resp.generateAudio === 'boolean'
            ? resp.generateAudio
            : null,
    };

    try {
      sessionStorage.setItem('regenerate-video', JSON.stringify(payload));
    } catch (error) {
      console.error('Unable to store regenerate params:', error);
    }

    router.push(`/ai-videos?regenerate=${Date.now()}`);
  };

  const handleVideoClick = () => {
    setShowModal(true);
    analytics.track('Video Opened', {
      video_id: video.id,
    });
  };

  const toggleModalMute = () => {
    if (modalVideoRef.current) {
      modalVideoRef.current.muted = !modalVideoRef.current.muted;
      setIsMuted(modalVideoRef.current.muted);
    }
  };

  const handleVideoError = () => {
    setVideoError(true);
    console.error('Video loading error');
  };

  const handleVideoLoadedData = () => {
    setIsVideoLoaded(true);
  };

  const handleRemove = async () => {
    if (window.confirm(t('Are you sure you want to delete this video?'))) {
      try {
        await mutation.mutateAsync('REMOVE');
        setIsDeleted(true);
        setShowModal(false);
        analytics.track('Video Deleted', {
          video_id: video.id,
        });
      } catch (error: any) {
        toastError(error);
      }
    }
  };

  // Show queue overlay for free users
  if (isFreePlan && isInQueue && (video as any)._queuedUntil) {
    return (
      <li className='relative'>
        <div className='group block aspect-[1/1] w-full overflow-hidden rounded-lg bg-gray-100 focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2 focus-within:ring-offset-gray-100 dark:bg-gray-800'>
          <SubscriptionPromptOverlay
            queuedUntil={(video as any)._queuedUntil}
            className='rounded-lg'
          />
        </div>
      </li>
    );
  }

  // Show loading state if video URL is not yet available
  if (!videoUrl) {
    return (
      <li className='relative'>
        <div className='group block aspect-[1/1] w-full overflow-hidden rounded-lg bg-gray-100 focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2 focus-within:ring-offset-gray-100 dark:bg-gray-800'>
          <div className='flex h-full items-center justify-center'>
            <div className='text-center'>
              <div className='mb-2 flex justify-center'>
                <Spinner className='animate-spin-fast h-8 w-8 text-primary-500' />
              </div>
              <span className='text-sm text-gray-500 dark:text-gray-400'>
                {t('Processing video...')}
              </span>
              <span className='mt-1 block text-sm text-gray-500 dark:text-gray-400'>
                {videoUpscale
                  ? t('Long videos can take several hours to upscale')
                  : t('This can take 2-5 minutes')}
              </span>
            </div>
          </div>
        </div>
      </li>
    );
  }

  const handleVotePlus = async (type: string) => {
    try {
      await mutation.mutateAsync(type);
      analytics.track('Video Vote', {
        video_id: video.id,
        type: type,
      });
    } catch (error: any) {
      toastError(error);
    }
  };

  if (isDeleted) return null;

  return (
    <li className='relative'>
      {miniMaxUpgradeId !== null && (
        <MiniMaxVideoUpgradeModal
          key={miniMaxUpgradeId}
          videoId={miniMaxUpgradeId}
          onClose={() => setMiniMaxUpgradeId(null)}
          onSubmitted={() => {
            queryClient.invalidateQueries(['video-generator-videos']);
            window.dispatchEvent(new Event('credits-refreshed'));
          }}
        />
      )}
      <Modal
        onClose={() => setShowShareModal(false)}
        showModal={showShareModal}
        className='overflow-auto sm:max-w-2xl'
      >
        <ShareVideoModal video={video} />
      </Modal>
      <Modal
        onClose={() => setEditSeed(null)}
        showModal={!!editSeed}
        className='mt-5 sm:max-w-6xl'
      >
        {editSeedForRender && (
          <VideoEditModal
            key={editSeedForRender.videoId}
            videoId={editSeedForRender.videoId}
            videoUrl={editSeedForRender.videoUrl}
            initialInstructions={editSeedForRender.instructions}
            initialReferenceImages={editSeedForRender.referenceImages}
            initialTier={editSeedForRender.tier}
            initialResolution={editSeedForRender.resolution}
            onClose={() => setEditSeed(null)}
            onSubmitted={() => {
              // The generator gallery polls every 10s; invalidating makes the
              // pending row show up immediately instead.
              queryClient.invalidateQueries(['video-generator-videos']);
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new Event('credits-refreshed'));
              }
            }}
          />
        )}
      </Modal>
      <Modal
        onClose={() => setShowModal(false)}
        showModal={showModal}
        className='mt-5 overflow-y-auto sm:max-w-6xl'
      >
        <main className='mx-auto'>
          <div className='grid grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8'>
            <div className='flex flex-col text-gray-500'>
              {video.model && (
                <p className='mb-3'>
                  <strong>{t('Model')}:</strong>{' '}
                  <span className='pr-3'>{video.model}</span>
                </p>
              )}
              {/* Free-trial videos carry no charge (used_credits 0) — hide
                  the row instead of showing a misleading price. */}
              {Number(video.used_credits) > 0 && (
                <p className='mb-3'>
                  <strong>{t('Credits used')}:</strong>{' '}
                  <span className='pr-3'>{video.used_credits}</span>
                </p>
              )}
              <p className='mb-3'>
                <strong>{t('Prompt')}:</strong>{' '}
                <span className='pr-3'>{video.prompt}</span>
                <CopyBtn
                  value={video.prompt || ''}
                  showText={false}
                  variant='simple'
                />
              </p>
              {!video.isFromExample && video.created_at && (
                <p>
                  <strong>{t('Created At')}:</strong>{' '}
                  {formatDate(video.created_at)}
                </p>
              )}

              {video.user_id === user?.id && (
                <div className='mt-5 flex'>
                  <p>
                    <strong>{t('Share with Team')}</strong>
                  </p>
                  <div className='ml-2'>
                    <Toggle
                      initialValue={shareWithTeam}
                      onChange={handleShareWithTeam}
                    />
                  </div>
                </div>
              )}

              {canEditWithAI && (
                <Button
                  className='mt-10 flex items-center justify-center'
                  onClick={handleEditWithAI}
                >
                  <SparklesIcon className='mr-2 h-6 w-6' />
                  {t('Edit with AI')}
                </Button>
              )}

              {canEditWithAI && (
                <Button
                  variant='outline'
                  className='mt-5 flex items-center justify-center'
                  onClick={handleUpscaleVideo}
                >
                  <FaExpand className='mr-2 h-5 w-5' />
                  {t('Upscale video')}
                </Button>
              )}

              {canUpgradeWithMiniMax && (
                <Button
                  variant='outline'
                  className='mt-5 flex items-center justify-center'
                  onClick={handleMiniMaxUpgrade}
                >
                  <FaExpand className='mr-2 h-5 w-5' />
                  {t('Upgrade to 2K · MiniMax')}
                </Button>
              )}

              {canExtendVideo && (
                <Button
                  variant='outline'
                  className='mt-5 flex items-center justify-center'
                  onClick={handleExtendVideo}
                >
                  <FilmIcon className='mr-2 h-6 w-6' />
                  {t('Extend video')}
                </Button>
              )}

              {!video.isFromExample && (
                <Button
                  variant='outline'
                  className={`${
                    canEditWithAI ? 'mt-5' : 'mt-10'
                  } flex items-center justify-center`}
                  onClick={handleRegenerate}
                >
                  <RefreshIcon className='mr-2 h-6 w-6' />
                  {isUpscaleResult
                    ? t('Upscale again')
                    : isEditResult
                      ? t('Change these edits')
                      : isExtendResult
                        ? t('Extend again')
                        : t('Regenerate')}
                </Button>
              )}

              {!isFailedGeneration && (
                <>
                  <Button
                    variant='outline'
                    className='mt-5 flex items-center justify-center'
                    onClick={handleDownload}
                  >
                    <DownloadIcon className='mr-2 h-6 w-6' />
                    {t('Download video')}
                  </Button>

                  {video.original_video_url && (
                    <Button
                      variant='outline'
                      className='mt-5 flex items-center justify-center'
                      onClick={() =>
                        window.open(video.original_video_url, '_blank')
                      }
                    >
                      <ExternalLinkIcon className='mr-2 h-6 w-6' />
                      {t('Video without sound effects')}
                    </Button>
                  )}
                </>
              )}
            </div>
            <div className='relative md:col-span-2'>
              {isFailedGeneration ? (
                <div className='flex h-full w-full items-center justify-center rounded-lg bg-gray-100 p-8 dark:bg-gray-800'>
                  <div className='text-center'>
                    <ExclamationCircleIcon className='mx-auto mb-4 h-16 w-16 text-red-500' />
                    <h3 className='mb-2 text-xl font-medium text-gray-900 dark:text-white'>
                      {t('Generation Failed')}
                    </h3>
                    <p className='mb-6 text-gray-500 dark:text-gray-400'>
                      {t(
                        'The prompt could not be submitted. This prompt contains sensitive words that violate usage guidelines.',
                      )}
                    </p>
                    <div className='rounded-md bg-white p-4 shadow-sm dark:bg-gray-700'>
                      <p className='text-sm text-gray-500 dark:text-gray-300'>
                        {t(
                          'Try rephrasing the prompt or try a different model:',
                        )}
                      </p>
                      <p className='mt-2 font-medium text-primary-500'>
                        Kling, Seedance, LTX, Hailuo
                      </p>
                    </div>
                  </div>
                </div>
              ) : videoError ? (
                <div className='flex h-full w-full items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800'>
                  <div className='text-center'>
                    <p className='text-gray-500 dark:text-gray-400'>
                      {t('Unable to load video')}
                    </p>
                    <button
                      onClick={() => window.open(videoUrl, '_blank')}
                      className='mt-2 text-primary-500 hover:text-primary-600'
                    >
                      {t('Open in new tab')}
                    </button>
                  </div>
                </div>
              ) : (
                <video
                  ref={modalVideoRef}
                  src={videoUrl}
                  className='w-full rounded-lg object-contain max-h-[70vh]'
                  controls
                  loop
                  playsInline
                  muted={isMuted}
                  autoPlay={true}
                  onError={handleVideoError}
                />
              )}
            </div>
          </div>
        </main>
      </Modal>

      <div
        className={clsxm(
          'group relative block aspect-[1/1] w-full overflow-hidden rounded-lg bg-gray-100 focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2 focus-within:ring-offset-gray-100 dark:bg-gray-800',
          mediaTileSelectionClassName(selection),
        )}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {video.user_id && video.user_id === user?.id && shareWithTeam && (
          <span
            className={clsxm(
              'absolute left-2 z-10 inline-flex items-center rounded-md bg-green-50 bg-opacity-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20',
              // The top-left corner belongs to the selection check.
              selection.enabled ? 'top-10' : 'top-2',
            )}
          >
            {t('Shared')}
          </span>
        )}
        {video.user_id && video.user_id !== user?.id && (
          <span
            className={clsxm(
              'absolute left-2 z-10 inline-flex items-center rounded-md bg-primary-50 bg-opacity-50 px-2 py-1 text-xs font-medium text-primary-700 ring-1 ring-inset ring-primary-600/20',
              selection.enabled ? 'top-10' : 'top-2',
            )}
          >
            {video.profiles?.first_name || video.profiles?.last_name
              ? `${video.profiles.first_name || ''} ${video.profiles.last_name || ''}`.trim()
              : video.profiles?.username || t('Team')}
          </span>
        )}
        <div className='relative h-full'>
          {isFailedGeneration ? (
            <div className='flex h-full w-full items-center justify-center bg-gray-50 p-4 dark:bg-gray-900'>
              <div className='text-center'>
                <ExclamationCircleIcon className='mx-auto mb-2 h-8 w-8 text-red-500' />
                <p className='mb-1 text-sm font-medium text-gray-900 dark:text-white'>
                  {t('Generation Failed')}
                </p>
                <p className='mb-3 line-clamp-2 text-xs text-gray-500 dark:text-gray-400'>
                  {t('Policy violation. Try different model or prompt.')}
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowModal(true);
                  }}
                  className='text-xs text-primary-500 underline hover:text-primary-600'
                >
                  {t('View details')}
                </button>
              </div>
            </div>
          ) : videoError ? (
            <div className='flex h-full w-full items-center justify-center'>
              <div className='text-center p-4'>
                <PlayIcon className='h-8 w-8 text-gray-400 mx-auto mb-2' />
                <p className='text-sm text-gray-500 dark:text-gray-400'>
                  {t('Video unavailable')}
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowModal(true);
                  }}
                  className='mt-1 text-xs text-primary-500 hover:text-primary-600'
                >
                  {t('View details')}
                </button>
              </div>
            </div>
          ) : (
            <>
              <video
                ref={videoRef}
                src={isiOS ? `${videoUrl}#t=0.001` : videoUrl}
                className='h-full w-full object-cover'
                loop
                playsInline
                muted={true}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onError={handleVideoError}
                onLoadedData={handleVideoLoadedData}
                preload='metadata'
              />

              {/* Loading overlay */}
              {!isVideoLoaded && !videoError && (
                <div className='absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800'>
                  <Spinner className='h-8 w-8 text-gray-400' />
                </div>
              )}

              {/* Play button overlay - always visible on hover or iOS */}
              {!selection.active && (
                <button
                  onClick={handleVideoClick}
                  className={`absolute inset-0 flex items-center justify-center bg-black transition-all duration-200 ${
                    isiOS
                      ? 'bg-opacity-20'
                      : 'bg-opacity-0 group-hover:bg-opacity-40'
                  }`}
                  aria-label={t('Play video')}
                >
                  <div
                    className={`rounded-full bg-white p-3 transition-all duration-200 ${
                      isiOS
                        ? 'bg-opacity-90 opacity-100'
                        : 'bg-opacity-90 opacity-0 group-hover:opacity-100 group-hover:scale-110'
                    }`}
                  >
                    <PlayIcon className='h-6 w-6 text-gray-900' />
                  </div>
                </button>
              )}
            </>
          )}
        </div>

        {!video.isFromExample && !selection.active && (
          <Menu as='div' className='absolute right-2 top-2 z-10'>
            <Menu.Button
              className='inline-flex items-center rounded-full bg-white p-2 text-gray-400 shadow-sm hover:bg-gray-50 hover:text-gray-500 focus:outline-none'
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <span className='sr-only'>{t('Open options')}</span>
              <DotsVerticalIcon className='h-5 w-5' aria-hidden='true' />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter='transition ease-out duration-100'
              enterFrom='transform opacity-0 scale-95'
              enterTo='transform opacity-100 scale-100'
              leave='transition ease-in duration-75'
              leaveFrom='transform opacity-100 scale-100'
              leaveTo='transform opacity-0 scale-95'
            >
              <Menu.Items className='absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none'>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowModal(true);
                      }}
                      className={`${
                        active ? 'bg-gray-100' : ''
                      } flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                    >
                      <FaExpand className='mr-3 h-5 w-5 text-gray-400' />
                      {t('Open details')}
                    </button>
                  )}
                </Menu.Item>
                {canEditWithAI && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditWithAI();
                        }}
                        className={`${
                          active ? 'bg-gray-100' : ''
                        } flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                      >
                        <SparklesIcon className='mr-3 h-5 w-5 text-primary-500' />
                        {t('Edit with AI')}
                      </button>
                    )}
                  </Menu.Item>
                )}
                {canEditWithAI && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUpscaleVideo();
                        }}
                        className={`${active ? 'bg-gray-100' : ''} flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                      >
                        <FaExpand className='mr-3 h-5 w-5 text-gray-400' />
                        {t('Upscale video')}
                      </button>
                    )}
                  </Menu.Item>
                )}
                {canUpgradeWithMiniMax && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMiniMaxUpgrade();
                        }}
                        className={`${active ? 'bg-gray-100' : ''} flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                      >
                        <FaExpand className='mr-3 h-5 w-5 text-gray-400' />
                        {t('Upgrade to 2K · MiniMax')}
                      </button>
                    )}
                  </Menu.Item>
                )}
                {canExtendVideo && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExtendVideo();
                        }}
                        className={`${
                          active ? 'bg-gray-100' : ''
                        } flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                      >
                        <FilmIcon className='mr-3 h-5 w-5 text-gray-400' />
                        {t('Extend video')}
                      </button>
                    )}
                  </Menu.Item>
                )}
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRegenerate();
                      }}
                      className={`${
                        active ? 'bg-gray-100' : ''
                      } flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                    >
                      <RefreshIcon className='mr-3 h-5 w-5 text-gray-400' />
                      {isUpscaleResult
                        ? t('Upscale again')
                        : isEditResult
                          ? t('Change these edits')
                          : isExtendResult
                            ? t('Extend again')
                            : t('Regenerate')}
                    </button>
                  )}
                </Menu.Item>
                {!isFailedGeneration && (
                  <>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload();
                          }}
                          className={`${
                            active ? 'bg-gray-100' : ''
                          } flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                        >
                          <DownloadIcon className='mr-3 h-5 w-5 text-gray-400' />
                          {t('Download')}
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowShareModal(true);
                          }}
                          className={`${
                            active ? 'bg-gray-100' : ''
                          } flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                        >
                          <ShareIcon className='mr-3 h-5 w-5 text-gray-400' />
                          {t('Share')}
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVotePlus('VOTE_PLUS');
                          }}
                          className={`${
                            active ? 'bg-gray-100' : ''
                          } flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                        >
                          <ThumbUpIcon className='mr-3 h-5 w-5 text-gray-400' />
                          {t('Good video')}
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVotePlus('VOTE_MINUS');
                          }}
                          className={`${
                            active ? 'bg-gray-100' : ''
                          } flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                        >
                          <ThumbDownIcon className='mr-3 h-5 w-5 text-gray-400' />
                          {t('Bad video')}
                        </button>
                      )}
                    </Menu.Item>
                  </>
                )}
                {!video.isFromExample && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemove();
                        }}
                        className={`${
                          active ? 'bg-gray-100' : ''
                        } flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-red-50`}
                      >
                        <TrashIcon className='mr-3 h-5 w-5 text-red-500' />
                        {t('Delete')}
                      </button>
                    )}
                  </Menu.Item>
                )}
              </Menu.Items>
            </Transition>
          </Menu>
        )}
        <MediaTileSelectionLayer
          selection={selection}
          label={video.user_prompt || video.prompt}
        />
      </div>
    </li>
  );
};

//memoized export
export default memo(VideoOutput);
